// src/pages/superadmin/OnboardingQueue.jsx
//
// AXO AI / Enterprise onboarding queue. New signups save a card but are not
// active or billed until AXO maps their services to an AXO product here.
// Completing onboarding creates the subscription from the saved card.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiService from "../../services/api";
import NotificationBell from "../../components/NotificationBell";

const PRODUCT_OPTIONS = [
  { value: "", label: "Select product" },
  { value: "wizzyl", label: "Wizzyl (Website)" },
  { value: "axo_signal", label: "AXO Signal (Growth)" },
  { value: "6ela", label: "6ELA (Finance)" },
  { value: "telco", label: "Telco (Comms)" },
];

const styles = `
  .onb-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); }
  .onb-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07); position: sticky; top: 0; z-index: 20; }
  .onb-header-inner { max-width: 1100px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .onb-brand { display: flex; align-items: center; gap: 12px; color: #fff; font-weight: 600; }
  .onb-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.07); color: #fff; text-decoration: none; }
  .onb-main { max-width: 1100px; margin: 0 auto; padding: 28px; display: flex; flex-direction: column; gap: 18px; }
  .onb-lead { font-size: 13px; color: var(--text-muted); margin-top: -8px; }
  .onb-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
  .onb-card-head { padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--surface-raised); display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .onb-title { font-size: 15px; font-weight: 700; }
  .onb-sub { margin-top: 3px; font-size: 12px; color: var(--text-muted); }
  .onb-meta { display: flex; gap: 14px; flex-wrap: wrap; font-size: 11.5px; color: var(--text-faint); margin-top: 8px; }
  .onb-body { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
  .onb-service { border: 1px solid var(--border); border-radius: 10px; padding: 12px; display: flex; flex-direction: column; gap: 10px; background: var(--surface-sub); }
  .onb-service-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .onb-service-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-faint); font-family: var(--mono); }
  .onb-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .onb-field label { display: block; font-size: 11px; font-weight: 600; color: var(--text-muted); margin-bottom: 4px; }
  .onb-input { width: 100%; padding: 8px 10px; border: 1px solid var(--border); border-radius: 7px; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 12.5px; box-sizing: border-box; }
  .onb-input:focus { outline: none; border-color: var(--accent-mid); box-shadow: 0 0 0 2px var(--accent-dim); }
  .onb-textarea { min-height: 64px; resize: vertical; }
  .onb-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .onb-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 34px; padding: 0 14px; border-radius: 8px; border: 1px solid transparent; font-size: 12px; font-weight: 600; cursor: pointer; font-family: var(--sans); }
  .onb-btn-primary { background: var(--accent); color: #fff; }
  .onb-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .onb-btn-ghost { background: transparent; border-color: var(--border); color: var(--text-muted); }
  .onb-btn-danger { background: transparent; border-color: var(--red-border); color: var(--red); }
  .onb-btn-sm { min-height: 28px; padding: 0 10px; font-size: 11px; }
  .onb-empty { padding: 48px 20px; text-align: center; color: var(--text-faint); font-size: 13px; }
  .onb-notice { padding: 10px 14px; border-radius: 8px; font-size: 12.5px; }
  .onb-notice.ok { background: var(--green-dim); border: 1px solid var(--green-border); color: var(--green); }
  .onb-notice.err { background: var(--red-dim); border: 1px solid var(--red-border); color: var(--red); }
  .onb-warn { font-size: 11px; color: var(--amber); }
  @media (max-width: 720px) { .onb-grid { grid-template-columns: 1fr; } .onb-main { padding: 16px; } }
`;

function traitText(traits) {
  return Array.isArray(traits) ? traits.join(", ") : "";
}

export default function OnboardingQueue() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiService.getOnboardingQueue({ includeActive: true });
      setQueue(res.data || []);
      setError("");
    } catch (err) {
      setError(
        err.response?.data?.message || "Couldn't load the onboarding queue",
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // If the customer edits their services in another tab, re-read the queue as
  // soon as the admin comes back to this page.
  useEffect(() => {
    const onFocus = () => load({ silent: true });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const patchCustomer = (customerId, updater) => {
    setQueue((prev) =>
      prev.map((c) => (c.id === customerId ? updater(c) : c)),
    );
  };

  const updateService = (customerId, index, key, value) => {
    patchCustomer(customerId, (c) => ({
      ...c,
      services: c.services.map((svc, i) =>
        i === index ? { ...svc, [key]: value } : svc,
      ),
    }));
  };

  const addService = (customerId) => {
    patchCustomer(customerId, (c) => ({
      ...c,
      services: [
        ...(c.services || []),
        {
          name: "",
          description: "",
          traits: [],
          productKey: "",
          priceGbp: "",
        },
      ],
    }));
  };

  const removeService = (customerId, index) => {
    patchCustomer(customerId, (c) => ({
      ...c,
      services: c.services.filter((_, i) => i !== index),
    }));
  };

  const saveMapping = async (customer, { quiet = false } = {}) => {
    setSavingId(customer.id);
    setNotice(null);
    try {
      const res = await apiService.saveOnboardingMapping(
        customer.id,
        customer.services,
      );
      if (!quiet) {
        setNotice({
          type: "ok",
          text: `Service mapping saved for ${customer.name}.`,
        });
      }
      return res.data?.services;
    } finally {
      setSavingId(null);
    }
  };

  const handleSave = async (customer) => {
    try {
      await saveMapping(customer);
      await load();
    } catch (err) {
      setNotice({
        type: "err",
        text:
          err.response?.data?.message || "Couldn't save the service mapping.",
      });
    }
  };

  const handleComplete = async (customer) => {
    if (
      !window.confirm(
        `Complete onboarding for ${customer.name} and start billing now?`,
      )
    ) {
      return;
    }
    setCompletingId(customer.id);
    setNotice(null);
    try {
      await saveMapping(customer, { quiet: true });
      await apiService.completeOnboarding(customer.id);
      setNotice({
        type: "ok",
        text: `${customer.name} is live — the subscription has started.`,
      });
      await load();
    } catch (err) {
      setNotice({
        type: "err",
        text:
          err.response?.data?.message ||
          "Couldn't complete onboarding. Check the mapping and Stripe.",
      });
    } finally {
      setCompletingId(null);
    }
  };

  const unmapped = (customer) =>
    (customer.services || []).filter(
      (svc) => !String(svc.name || "").trim() || !svc.productKey,
    );

  return (
    <div className="onb-root">
      <style>{styles}</style>
      <header className="onb-header">
        <div className="onb-header-inner">
          <div className="onb-brand">
            <Link to="/super-admin" className="onb-back" title="Back">
              ←
            </Link>
            Onboarding queue
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <NotificationBell />
            <button
              type="button"
              onClick={() => load({ silent: true })}
              style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.14)",
                color: "#fff",
                borderRadius: 8,
                padding: "7px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Refresh
            </button>
            <Link
              to="/super-admin/customers"
              style={{ color: "#fff", fontSize: 12.5 }}
            >
              Customers
            </Link>
          </div>
        </div>
      </header>

      <main className="onb-main">
        <div>
          <h1 style={{ margin: 0, fontSize: 20 }}>AXO AI onboarding</h1>
          <p className="onb-lead">
            Pending AI / Enterprise signups save a card but stay inactive and
            unbilled until their services are mapped and onboarding is
            completed. Active customers are also listed so their services can
            be remapped without touching billing.
          </p>
        </div>

        {notice && <div className={`onb-notice ${notice.type}`}>{notice.text}</div>}
        {error && <div className="onb-notice err">{error}</div>}

        {loading ? (
          <div className="onb-empty">Loading onboarding queue…</div>
        ) : queue.length === 0 ? (
          <div className="onb-empty">No customers are awaiting onboarding.</div>
        ) : (
          queue.map((customer) => {
            const missing = unmapped(customer);
            return (
              <section className="onb-card" key={customer.id}>
                <div className="onb-card-head">
                  <div>
                    <div className="onb-title">{customer.name}</div>
                    <div className="onb-sub">
                      {customer.planName || customer.planId || "AXO AI"} ·{" "}
                      {customer.owner?.email || "No owner email"}
                    </div>
                    <div className="onb-meta">
                      <span>
                        Signed up{" "}
                        {customer.createdAt
                          ? new Date(customer.createdAt).toLocaleDateString(
                              "en-GB",
                            )
                          : "—"}
                      </span>
                      <span>{customer.services.length} service(s)</span>
                      {customer.servicesUpdatedAt && (
                        <span>
                          Services updated{" "}
                          {new Date(
                            customer.servicesUpdatedAt,
                          ).toLocaleString("en-GB", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      )}
                      <span>
                        {customer.isPending
                          ? "Pending onboarding · card saved · billing not started"
                          : "Active customer · mapping update only"}
                      </span>
                    </div>
                  </div>
                  <div className="onb-actions">
                    <button
                      type="button"
                      className="onb-btn onb-btn-ghost"
                      onClick={() => handleSave(customer)}
                      disabled={savingId === customer.id}
                    >
                      {savingId === customer.id ? "Saving…" : "Save mapping"}
                    </button>
                    {customer.isPending && (
                      <button
                        type="button"
                        className="onb-btn onb-btn-primary"
                        onClick={() => handleComplete(customer)}
                        disabled={
                          completingId === customer.id || missing.length > 0
                        }
                        title={
                          missing.length
                            ? "Give every service a name and map it first"
                            : "Create subscription and activate"
                        }
                      >
                        {completingId === customer.id
                          ? "Starting subscription…"
                          : "Complete onboarding & start billing"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="onb-body">
                  {missing.length > 0 && (
                    <div className="onb-warn">
                      Give every service a name and map it to a product before
                      completing.
                    </div>
                  )}

                  {(customer.services || []).map((svc, index) => (
                    <div className="onb-service" key={svc.id || index}>
                      <div className="onb-service-head">
                        <span className="onb-service-title">
                          Service {index + 1}
                        </span>
                        <button
                          type="button"
                          className="onb-btn onb-btn-danger onb-btn-sm"
                          onClick={() => removeService(customer.id, index)}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="onb-grid">
                        <div className="onb-field">
                          <label>Service name</label>
                          <input
                            className="onb-input"
                            value={svc.name || ""}
                            onChange={(e) =>
                              updateService(
                                customer.id,
                                index,
                                "name",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. Commercial cleaning"
                          />
                        </div>
                        <div className="onb-field">
                          <label>Typical deal value (£)</label>
                          <input
                            className="onb-input"
                            type="number"
                            min="0"
                            value={svc.priceGbp ?? ""}
                            onChange={(e) =>
                              updateService(
                                customer.id,
                                index,
                                "priceGbp",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. 2500"
                          />
                        </div>
                      </div>

                      <div className="onb-field">
                        <label>What it includes</label>
                        <textarea
                          className="onb-input onb-textarea"
                          value={svc.description || ""}
                          onChange={(e) =>
                            updateService(
                              customer.id,
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                        />
                      </div>

                      <div className="onb-grid">
                        <div className="onb-field">
                          <label>Traits</label>
                          <input
                            className="onb-input"
                            value={traitText(svc.traits)}
                            onChange={(e) =>
                              updateService(
                                customer.id,
                                index,
                                "traits",
                                e.target.value
                                  .split(",")
                                  .map((t) => t.trim())
                                  .filter(Boolean),
                              )
                            }
                            placeholder="comma separated"
                          />
                        </div>
                        <div className="onb-field">
                          <label>Map to AXO product</label>
                          <select
                            className="onb-input"
                            value={svc.productKey || ""}
                            onChange={(e) =>
                              updateService(
                                customer.id,
                                index,
                                "productKey",
                                e.target.value,
                              )
                            }
                          >
                            {PRODUCT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div>
                    <button
                      type="button"
                      className="onb-btn onb-btn-ghost onb-btn-sm"
                      onClick={() => addService(customer.id)}
                    >
                      + Add service
                    </button>
                  </div>
                </div>
              </section>
            );
          })
        )}
      </main>
    </div>
  );
}
